import { useCallback, useEffect, useRef, useState } from 'react'
import {
  adminApi, ApiError,
  type AdminFeedbackDetail, type AdminFeedbackItem, type AdminOrganization,
  type FeedbackKind, type FeedbackStatus,
} from '../lib/api'
import {
  KindChip, STATUS_ORDER, STATUS_WORDS, StatusPill, Thread, day, stamp,
} from '../pages/FeedbackParts'
import '../pages/feedback.css'

/** THE OPERATOR'S SIDE OF THE FEEDBACK BOARD (migration 065): every report
 *  from every customer, above all organizations like the rest of this
 *  console.
 *
 *  A STATUS SET HERE IS SEEN BY THE CUSTOMER AT ONCE AND MAILS THE REPORTER,
 *  and it stays in his thread for good. So it is chosen and then CONFIRMED
 *  with a button -- a select that committed on change turned one slip of the
 *  arrow key into a permanent line in a customer's conversation.
 *
 *  A PRIVATE NOTE never reaches the customer: it is not sent to his side at
 *  all, and it does not move his list or light his mark. */

const POLL_MS = 60_000
type Filter = 'all' | 'unread' | FeedbackStatus

function AddForCustomer({ orgs, onDone, onCancel }: {
  orgs: AdminOrganization[]
  onDone: (item: AdminFeedbackDetail) => void
  onCancel: () => void
}) {
  const [orgId, setOrgId] = useState(orgs[0]?.id ?? '')
  const [kind, setKind] = useState<FeedbackKind>('feature')
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [status, setStatus] = useState<FeedbackStatus>('new')
  const [who, setWho] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function save() {
    if (busy || !orgId || !title.trim()) return
    setBusy(true)
    setError(null)
    try {
      onDone(await adminApi.feedbackCreate(orgId, {
        kind, title: title.trim(), body: body.trim() || null, status,
        reported_by_label: who.trim() || null,
      }))
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not add it.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="fb-bd">
      <div className="fb-hint" style={{ marginBottom: 12 }}>
        For something a customer told you on the phone. It has no login behind it, so at
        the customer it is seen by whoever there may see every report.
      </div>
      <div className="fb-field">
        <label>Customer</label>
        <select value={orgId} onChange={(e) => setOrgId(e.target.value)} disabled={busy}>
          {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>
      </div>
      <div className="fb-field">
        <label>What is it?</label>
        <div className="fb-seg">
          <button type="button" className={kind === 'bug' ? 'fb-on' : ''} onClick={() => setKind('bug')}>Bug</button>
          <button type="button" className={kind === 'feature' ? 'fb-on' : ''} onClick={() => setKind('feature')}>Feature</button>
        </div>
      </div>
      <div className="fb-field">
        <label>In one line</label>
        <input type="text" value={title} maxLength={200} disabled={busy}
               onChange={(e) => setTitle(e.target.value)} />
      </div>
      <div className="fb-field">
        <label>Description</label>
        <textarea rows={4} value={body} maxLength={8000} disabled={busy}
                  onChange={(e) => setBody(e.target.value)} />
      </div>
      <div className="fb-field">
        <label>Who said it</label>
        <input type="text" value={who} maxLength={200} disabled={busy}
               placeholder="Joel, on the phone" onChange={(e) => setWho(e.target.value)} />
      </div>
      <div className="fb-field">
        <label>Status</label>
        <select value={status} disabled={busy}
                onChange={(e) => setStatus(e.target.value as FeedbackStatus)}>
          {STATUS_ORDER.map((s) => <option key={s} value={s}>{STATUS_WORDS[s]}</option>)}
        </select>
      </div>
      {error && <div className="fb-note fb-bad">{error}</div>}
      <div className="fb-line" style={{ marginTop: 12 }}>
        <button type="button" className="fb-btn fb-primary" disabled={busy || !title.trim() || !orgId}
                onClick={save}>{busy ? 'Adding…' : 'Add it'}</button>
        <button type="button" className="fb-btn" disabled={busy} onClick={onCancel}>Cancel</button>
      </div>
    </div>
  )
}

export function AdminFeedback({ orgs, onCount, onClose }: {
  orgs: AdminOrganization[]
  /** How many reports hold something the operator has not read. */
  onCount?: (n: number) => void
  /** Back to the customer that was open. */
  onClose: () => void
}) {
  const [items, setItems] = useState<AdminFeedbackItem[]>([])
  const [counts, setCounts] = useState<Record<string, number>>({})
  const [filter, setFilter] = useState<Filter>('all')
  const [orgFilter, setOrgFilter] = useState('')
  const [open, setOpen] = useState<{ orgId: string; id: string } | null>(null)
  const [detail, setDetail] = useState<AdminFeedbackDetail | null>(null)
  const [nextStatus, setNextStatus] = useState<FeedbackStatus>('new')
  const [adding, setAdding] = useState(false)
  const [text, setText] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null)
  const [email, setEmail] = useState('')
  const [savedEmail, setSavedEmail] = useState<string | null>(null)
  const [emailNote, setEmailNote] = useState<string | null>(null)
  const onCountRef = useRef(onCount)
  useEffect(() => { onCountRef.current = onCount }, [onCount])
  /** Which report is open, for a response that arrives late: one for a
   *  report no longer open is dropped rather than drawn. */
  const openRef = useRef<string | null>(null)
  const openOrg = open?.orgId ?? null
  const openId = open?.id ?? null

  const load = useCallback(async () => {
    try {
      const board = await adminApi.feedback()
      setItems(board.items)
      setCounts(board.counts)
      onCountRef.current?.(board.unread)
      setError(null)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not load the reports.')
    }
  }, [])
  useEffect(() => {
    load()
    const t = setInterval(load, POLL_MS)
    return () => clearInterval(t)
  }, [load])
  useEffect(() => {
    adminApi.feedbackSettings()
      .then((s) => { setSavedEmail(s.notify_email); setEmail(s.notify_email ?? '') })
      .catch(() => {})
  }, [])

  const show = useCallback((d: AdminFeedbackDetail) => {
    setDetail(d)
    setNextStatus(d.status)
  }, [])
  const loadDetail = useCallback(async (orgId: string, id: string) => {
    try {
      const d = await adminApi.feedbackRead(orgId, id)
      if (openRef.current !== id) return
      show(d)
      // Opening it read it: the list's mark and the tab's count follow.
      load()
    } catch (e) {
      if (openRef.current !== id) return
      setNote({ ok: false, text: e instanceof ApiError ? e.message : 'Could not open it.' })
    }
  }, [load, show])
  // On the IDS, not on the `open` object: a second click on the row already
  // open makes a new object, and must not wipe a half-typed reply.
  useEffect(() => {
    openRef.current = openId
    setText('')
    setNote(null)
    setDetail(null)
    if (openOrg && openId) loadDetail(openOrg, openId)
  }, [openOrg, openId, loadDetail])

  async function act(run: () => Promise<AdminFeedbackDetail>, done: string): Promise<boolean> {
    if (busy || !detail) return false
    const { organization_id: orgId, id } = detail
    setBusy(true)
    setNote(null)
    try {
      const d = await run()
      if (openRef.current === id) { show(d); setNote({ ok: true, text: done }) }
      load()
      return true
    } catch (e) {
      if (openRef.current === id) {
        setNote({ ok: false, text: e instanceof ApiError ? e.message : 'That did not go through.' })
        // A refusal usually means the report moved under us: show it as it is.
        adminApi.feedbackRead(orgId, id)
          .then((d) => { if (openRef.current === id) show(d) })
          .catch(() => {})
      }
      return false
    } finally {
      setBusy(false)
    }
  }

  async function saveEmail() {
    setEmailNote(null)
    try {
      const s = await adminApi.feedbackSettingsSave(email.trim() || null)
      setSavedEmail(s.notify_email)
      setEmail(s.notify_email ?? '')
      setEmailNote(s.notify_email ? 'Saved.' : 'Cleared: no mail is sent.')
    } catch (e) {
      setEmailNote(e instanceof ApiError ? e.message : 'Could not save it.')
    }
  }

  const shown = items.filter((i) =>
    (!orgFilter || i.organization_id === orgFilter)
    && (filter === 'all' || (filter === 'unread' ? i.unread : i.status === filter)))
  const total = items.length
  const unread = items.filter((i) => i.unread).length

  return (
    <div className="fb-console">
      <div className="fb-line">
        <h2 className="fb-title">Feedback</h2>
        <span className="fb-hint">Bug reports and feature requests from every customer.</span>
        <span className="fb-spacer" />
        <button type="button" className="fb-btn fb-sm"
                onClick={() => { setAdding(true); setOpen(null) }}>+ Add for a customer</button>
        <button type="button" className="fb-btn fb-sm" onClick={onClose}>Close</button>
      </div>

      <div className="fb-line">
        <div className="fb-field" style={{ margin: 0, flex: '0 1 340px' }}>
          <label>Email me every new report at</label>
          <input type="email" value={email} maxLength={254} placeholder="you@example.com"
                 onChange={(e) => setEmail(e.target.value)} />
        </div>
        <button type="button" className="fb-btn fb-sm" style={{ alignSelf: 'flex-end' }}
                disabled={(email.trim() || null) === savedEmail} onClick={saveEmail}>Save</button>
        <span className="fb-hint" style={{ alignSelf: 'flex-end', flex: '1 1 260px' }}>
          {emailNote ?? 'Sent through the reporting customer\'s own email channel. A customer with no email channel sends none; the count on this tab always shows.'}
        </span>
      </div>

      {error && <div className="fb-note fb-bad">{error}</div>}

      <div className="fb-counters">
        <button type="button" className={`fb-cnt ${filter === 'all' ? 'fb-on' : ''}`}
                onClick={() => setFilter('all')}>All <b>{total}</b></button>
        <button type="button" className={`fb-cnt ${filter === 'unread' ? 'fb-on' : ''}`}
                onClick={() => setFilter('unread')}>Unread <b>{unread}</b></button>
        {(['new', 'need_info', 'accepted', 'in_progress', 'done'] as FeedbackStatus[]).map((s) => (
          <button type="button" key={s} className={`fb-cnt ${filter === s ? 'fb-on' : ''}`}
                  onClick={() => setFilter(s)}>
            {s === 'need_info' ? 'Need info' : STATUS_WORDS[s]} <b>{counts[s] ?? 0}</b>
          </button>
        ))}
      </div>

      <div className={`fb fb-wide ${open || adding ? 'fb-showing' : ''}`}>
        <div className="fb-col fb-list">
          <h2>
            Reports <span className="fb-sub">{shown.length}</span>
            <span className="fb-spacer" />
            <select className="fb-select" value={orgFilter} onChange={(e) => setOrgFilter(e.target.value)}>
              <option value="">Every customer</option>
              {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
            <button type="button" className={`fb-btn fb-sm ${filter === 'declined' ? 'fb-primary' : ''}`}
                    onClick={() => setFilter(filter === 'declined' ? 'all' : 'declined')}>
              Declined {counts.declined ?? 0}
            </button>
          </h2>
          <div className="fb-scroll">
            {shown.length === 0 ? (
              <div className="fb-empty"><div className="fb-big">Nothing here</div></div>
            ) : (
              <table className="fb-table">
                <thead><tr><th>#</th><th>Customer</th><th>Report</th><th>Status</th></tr></thead>
                <tbody>
                  {shown.map((i) => (
                    <tr key={`${i.organization_id}/${i.id}`}
                        className={openId === i.id ? 'fb-on' : ''}
                        onClick={() => { setAdding(false); setOpen({ orgId: i.organization_id, id: i.id }) }}>
                      <td>{i.number}</td>
                      <td>{i.organization_slug}</td>
                      <td>
                        {i.unread && <span className="fb-dot" title="Not read yet" />}
                        <KindChip kind={i.kind} />{i.title}
                        <div className="fb-sub">
                          {i.reported_by_label ?? 'the organization'} · {day(i.created_at)}
                          {i.entered_by_operator ? ' · entered here' : ''}
                          {i.customer_unread ? ' · customer has not read your last' : ''}
                        </div>
                      </td>
                      <td><StatusPill status={i.status} at={i.status_at} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        <div className="fb-col fb-detail">
          {adding ? (
            <>
              <h2>Add for a customer</h2>
              <div className="fb-scroll">
                <AddForCustomer
                  orgs={orgs}
                  onCancel={() => setAdding(false)}
                  onDone={(item) => {
                    setAdding(false)
                    setOpen({ orgId: item.organization_id, id: item.id })
                    load()
                  }}
                />
              </div>
            </>
          ) : !detail ? (
            <div className="fb-empty"><div className="fb-big">{openId ? 'Loading…' : 'Pick a report'}</div></div>
          ) : (
            <>
              <h2>
                <button type="button" className="fb-btn fb-sm fb-back" onClick={() => setOpen(null)}>← Back</button>
                {detail.organization_name} · #{detail.number}
                <span className="fb-spacer" />
                <StatusPill status={detail.status} at={detail.status_at} />
              </h2>
              <div className="fb-scroll">
                <div className="fb-bd">
                  <div style={{ marginBottom: 10 }}>
                    <KindChip kind={detail.kind} /><b>{detail.title}</b>
                  </div>
                  <div className="fb-field">
                    <label>Status — the customer sees it, and is mailed, the moment you set it</label>
                    <div className="fb-line">
                      <select value={nextStatus} disabled={busy} style={{ flex: '1 1 200px', width: 'auto' }}
                              onChange={(e) => setNextStatus(e.target.value as FeedbackStatus)}>
                        {STATUS_ORDER.map((s) => <option key={s} value={s}>{STATUS_WORDS[s]}</option>)}
                      </select>
                      <button type="button" className="fb-btn fb-primary"
                              disabled={busy || nextStatus === detail.status}
                              onClick={() => act(
                                () => adminApi.feedbackStatus(detail.organization_id, detail.id, nextStatus),
                                `Moved to ${STATUS_WORDS[nextStatus]}.`)}>
                        Set status
                      </button>
                    </div>
                  </div>
                  <div className="fb-thread">
                    <div className="fb-say fb-them">
                      {detail.body || '(no description)'}
                      {detail.attachments.length > 0 && (
                        <div className="fb-shots">
                          {detail.attachments.map((a) => {
                            const src = adminApi.feedbackPictureUrl(detail.organization_id, a.id)
                            return (
                              <img key={a.id} src={src} alt="Screenshot sent with the report"
                                   onClick={() => window.open(src, '_blank', 'noopener')} />
                            )
                          })}
                        </div>
                      )}
                      <span className="fb-meta">
                        {detail.reported_by_label ?? 'the organization'} · {stamp(detail.created_at)}
                        {detail.screen ? ` · sent from: ${detail.screen}` : ''}
                        {detail.entered_by_operator ? ' · entered here' : ''}
                      </span>
                    </div>
                  </div>
                  <div style={{ height: 8 }} />
                  <Thread events={detail.events} forOperator />
                </div>
              </div>
              <div className="fb-foot">
                {note && <div className={`fb-note ${note.ok ? '' : 'fb-bad'}`}>{note.text}</div>}
                <div className="fb-field" style={{ marginBottom: 8 }}>
                  <textarea rows={3} value={text} disabled={busy}
                            placeholder="Write to the customer, or a note for yourself…"
                            onChange={(e) => setText(e.target.value)} />
                </div>
                <div className="fb-line">
                    <button type="button" className="fb-btn fb-primary" disabled={busy || !text.trim()}
                            onClick={async () => {
                              const ok = await act(
                                () => adminApi.feedbackReply(detail.organization_id, detail.id, text.trim(), false),
                                'Sent to the customer.')
                              if (ok) setText('')
                            }}>Reply to the customer</button>
                    <button type="button" className="fb-btn" disabled={busy || !text.trim()}
                            onClick={async () => {
                              const ok = await act(
                                () => adminApi.feedbackReply(detail.organization_id, detail.id, text.trim(), true),
                                'Kept as a private note.')
                              if (ok) setText('')
                            }}>Save as a private note</button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
